import { floorPlanRoomColour } from './shared/floorplan.js';
import type { FloorPlanElement, FloorPlanProject, FloorPlanRoom, FloorPlanSegment } from './shared/floorplan.js';

const escapeXml = (value: string) => value.replace(/[<>&'"]/g, (character) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', "'": '&apos;', '"': '&quot;' })[character] || character);

function segmentSvg(segment: FloorPlanSegment, threeD = false): string {
  const colour = segment.kind === 'wall' ? '#171717' : segment.kind === 'door' ? '#AE7839' : '#3e7180';
  const width = segment.kind === 'wall' ? 7 : 4;
  return `<line x1="${segment.start.x}" y1="${segment.start.y}" x2="${segment.end.x}" y2="${segment.end.y}" stroke="${colour}" stroke-width="${width}" stroke-linecap="${threeD ? 'butt' : 'round'}" ${segment.kind === 'window' ? 'stroke-dasharray="12 7"' : ''}/>`;
}

function roomSvg(room: FloorPlanRoom): string {
  const points = room.polygon.map((point) => `${point.x},${point.y}`).join(' ');
  const labelPoint = room.polygon[0] || { x: 0, y: 0 };
  return `<polygon points="${points}" fill="${floorPlanRoomColour(room.label)}" stroke="#AE7839" stroke-width="2"/><text x="${labelPoint.x + 8}" y="${labelPoint.y + 20}" fill="#171717" font-family="Arial" font-size="18">${escapeXml(room.label || 'Raum')}</text>`;
}

export function buildFloorPlanSvg(project: FloorPlanProject, width = 1200, height = 800): string {
  const background = project.backgroundVisible && project.backgroundDataUrl
    ? `<image href="${project.backgroundDataUrl}" x="0" y="0" width="${width}" height="${height}" preserveAspectRatio="none" opacity=".42"/>`
    : '';
  const elements = project.elements.map((element: FloorPlanElement) => element.kind === 'room' ? roomSvg(element) : segmentSvg(element)).join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"><rect width="100%" height="100%" fill="#F8F6F2"/>${background}<g>${elements}</g></svg>`;
}

export function isoPoint(point: { x: number; y: number }, z: number, rotation: number, zoom: number): { x: number; y: number } {
  const x = point.x / 1200 - .5;
  const y = point.y / 800 - .5;
  const radians = rotation * Math.PI / 180;
  const rx = x * Math.cos(radians) - y * Math.sin(radians);
  const ry = x * Math.sin(radians) + y * Math.cos(radians);
  const scale = 500 * zoom;
  return { x: 600 + (rx - ry) * scale, y: 520 + (rx + ry) * scale * .42 - z * scale * .7 };
}

function pointsString(points: Array<{ x: number; y: number }>): string { return points.map((point) => `${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(' '); }

export function buildFloorPlan3dSvg(project: FloorPlanProject, rotation = -20, zoom = 1): string {
  const height = project.assumptions.wallHeight / 8;
  const walls = project.elements.filter((element): element is FloorPlanSegment => element.kind === 'wall').map((wall) => {
    const length = Math.max(1, Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y));
    const thicknessPixels = Math.max(3, project.assumptions.wallThickness * 80);
    const normal = { x: -(wall.end.y - wall.start.y) / length * thicknessPixels, y: (wall.end.x - wall.start.x) / length * thicknessPixels };
    const corners = [
      { x: wall.start.x + normal.x, y: wall.start.y + normal.y },
      { x: wall.end.x + normal.x, y: wall.end.y + normal.y },
      { x: wall.end.x - normal.x, y: wall.end.y - normal.y },
      { x: wall.start.x - normal.x, y: wall.start.y - normal.y },
    ];
    const bottom = corners.map((point) => isoPoint(point, 0, rotation, zoom));
    const top = corners.map((point) => isoPoint(point, height, rotation, zoom));
    return `<polygon points="${pointsString([...bottom, ...top.slice().reverse()])}" fill="#C9B79F" stroke="#171717" stroke-width="2"/>`;
  }).join('');
  const rooms = project.elements.filter((element): element is FloorPlanRoom => element.kind === 'room').map((room) => `<polygon points="${pointsString(room.polygon.map((point) => isoPoint(point, 0, rotation, zoom)))}" fill="${floorPlanRoomColour(room.label)}" stroke="#AE7839" stroke-width="2"/>`).join('');
  const openings = project.elements.filter((element): element is FloorPlanSegment => element.kind === 'door' || element.kind === 'window').map((opening) => {
    const start = isoPoint(opening.start, opening.kind === 'window' ? height * .55 : .02, rotation, zoom);
    const end = isoPoint(opening.end, opening.kind === 'window' ? height * .55 : .02, rotation, zoom);
    return `<line x1="${start.x}" y1="${start.y}" x2="${end.x}" y2="${end.y}" stroke="${opening.kind === 'window' ? '#3e7180' : '#AE7839'}" stroke-width="8"/>`;
  }).join('');
  const furniture = (project.furniture || []).map(item => {
    const angle = item.rotation * Math.PI / 180;
    const corner = (x: number, y: number, z: number) => isoPoint({x: item.x + x*Math.cos(angle)-y*Math.sin(angle), y: item.y+x*Math.sin(angle)+y*Math.cos(angle)}, z, rotation, zoom);
    const box = (x: number, y: number, w: number, d: number, base: number, h: number) => {
      const bottom = [[x,y],[x+w,y],[x+w,y+d],[x,y+d]].map(([a,b])=>corner(a,b,base));
      const top = [[x,y],[x+w,y],[x+w,y+d],[x,y+d]].map(([a,b])=>corner(a,b,base+h));
      return [0,1,2,3].map(i=>`<polygon points="${pointsString([bottom[i]!,bottom[(i+1)%4]!,top[(i+1)%4]!,top[i]!])}" fill="#a6b5b9" stroke="#65777c"/>`).join('') + `<polygon points="${pointsString(top)}" fill="#d6e0df" stroke="#65777c"/>`;
    };
    const w=item.width,d=item.depth,h=item.height/250;
    let shape=box(-w/2,-d/2,w,d,0,h*(item.type==='table'?.85:.55));
    if (['sofa','armchair','chair'].includes(item.type)) shape+=box(-w/2,-d/2,w,d*.18,h*.55,h*.45);
    if (item.type==='bed') shape+=box(-w*.4,-d*.4,w*.8,d*.2,h*.55,h*.12);
    if (item.type==='wardrobe') shape=box(-w/2,-d/2,w,d,0,h);
    return `<g>${shape}</g>`;
  }).join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="800" viewBox="0 0 1200 800"><rect width="100%" height="100%" fill="#F8F6F2"/><polygon points="260,620 600,770 940,620 600,470" fill="#FFFFFF" stroke="#E3DED7"/><g>${rooms}${walls}${openings}${furniture}</g></svg>`;
}

export function downloadText(filename: string, content: string, type: string): void {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function downloadFloorPlanSvg(project: FloorPlanProject): void {
  downloadText('property-partners-grundriss.svg', buildFloorPlanSvg(project), 'image/svg+xml');
}

export async function downloadFloorPlanPng(project: FloorPlanProject): Promise<void> {
  const svg = buildFloorPlanSvg(project);
  const blob = new Blob([svg], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  try {
    const image = new Image();
    image.decoding = 'async';
    image.src = url;
    await image.decode();
    const canvas = document.createElement('canvas');
    canvas.width = 1200;
    canvas.height = 800;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('PNG-Export ist in diesem Browser nicht verfügbar.');
    context.drawImage(image, 0, 0);
    const png = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!png) throw new Error('PNG-Export ist fehlgeschlagen.');
    const pngUrl = URL.createObjectURL(png);
    const link = document.createElement('a');
    link.href = pngUrl;
    link.download = 'property-partners-grundriss.png';
    link.click();
    URL.revokeObjectURL(pngUrl);
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function downloadFloorPlan3dPng(project: FloorPlanProject, rotation: number, zoom: number): Promise<void> {
  const svg = buildFloorPlan3dSvg(project, rotation, zoom);
  const blob = new Blob([svg], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  try {
    const image = new Image(); image.src = url; await image.decode();
    const canvas = document.createElement('canvas'); canvas.width = 1200; canvas.height = 800;
    const context = canvas.getContext('2d'); if (!context) throw new Error('PNG-Export ist in diesem Browser nicht verfügbar.');
    context.drawImage(image, 0, 0);
    const png = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!png) throw new Error('PNG-Export ist fehlgeschlagen.');
    const pngUrl = URL.createObjectURL(png); const link = document.createElement('a'); link.href = pngUrl; link.download = 'property-partners-grundriss-3d.png'; link.click(); URL.revokeObjectURL(pngUrl);
  } finally { URL.revokeObjectURL(url); }
}
